import * as THREE from 'three';
import { Vec3 } from '@/core/math/Vec3';
import { ReferenceFrameManager } from '@/frames/ReferenceFrameManager';
import { ChainedPrediction, RouteArc } from '@/spacecraft/TrajectoryPredictor';
import { TrajectoryLine } from './TrajectoryLine';
import { WorldMarkerLayer } from './WorldMarkers';

/** Arc colour by how the arc ends, mirroring `TrajectoryLine`'s vocabulary. */
const ARC_COLOR: Record<RouteArc['terminator'], number> = {
  impact: 0xff4d4d,
  soiExit: 0xffcf5c,
  soiEntry: 0x8ce0a8,
  horizon: 0x7fd1ff,
};

const JUNCTION_COLOR = 0xffd166;

/**
 * The planned route of a transfer, drawn as the multi-arc path it actually is.
 *
 * This is the chained counterpart to `TrajectoryLine`, which draws a single
 * conic about one body. A route is several conics about *different* bodies,
 * and that is the whole difficulty: an arc about a moon and an arc about the
 * star are expressed in frames moving relative to each other, so there is no
 * single frame in which all their sample points simultaneously mean anything.
 *
 * The resolution is to convert each point using **its own time** into the
 * system inertial frame — the one place every arc agrees on where the vessel
 * physically is when it is there. That conversion does not depend on the
 * current time, only on the sample's, so it is cached per route and the
 * per-frame cost falls to one vector subtraction per point. Rebuilding is
 * keyed on route object identity, which is what makes it affordable to publish
 * a fresh provisional route on every tick of a solve.
 *
 * Colour follows the arc's terminator, so an impact or an escape reads at a
 * glance without checking a readout, and a dot marks every frame handover so
 * they look like the discrete events they are rather than a kink in a line.
 */
export class RouteOverlay {
  readonly markers = new WorldMarkerLayer(11);

  private readonly lines: TrajectoryLine[] = [];
  private source: ChainedPrediction | null = null;
  private arcs: { points: Vec3[]; color: number }[] = [];
  private junctions: { position: Vec3; color: number }[] = [];

  constructor(
    private readonly frames: ReferenceFrameManager,
    private readonly rootFrameId: string,
    private readonly scene: THREE.Object3D,
  ) {
    this.scene.add(this.markers.points);
  }

  /** Draws `route`, with the camera given in the system inertial frame. */
  show(route: ChainedPrediction, cameraInRoot: Vec3): void {
    if (route !== this.source) this.rebuild(route);

    const toRenderSpace = (p: Vec3): THREE.Vector3 => {
      const offset = Vec3.subVectors(p, cameraInRoot);
      return new THREE.Vector3(offset.x, offset.y, offset.z);
    };

    for (let i = 0; i < this.arcs.length; i++) {
      if (!this.lines[i]) {
        const line = new TrajectoryLine();
        this.lines[i] = line;
        this.scene.add(line.line);
      }
      this.lines[i].setPath(this.arcs[i].points.map(toRenderSpace), this.arcs[i].color);
    }
    for (let i = this.arcs.length; i < this.lines.length; i++) this.lines[i].setPath([], 0);

    this.markers.set(
      this.junctions.map((j) => ({ position: toRenderSpace(j.position), color: j.color })),
    );
  }

  hide(): void {
    for (const line of this.lines) line.setPath([], 0);
    this.markers.set([]);
  }

  private rebuild(route: ChainedPrediction): void {
    this.source = route;
    this.arcs = [];
    this.junctions = [];

    for (const arc of route.arcs) {
      if (arc.points.length < 2) continue;
      const points = arc.points.map((p, i) =>
        this.frames.transformPosition(p, arc.referenceBody.inertialFrameId, this.rootFrameId, arc.times[i]),
      );
      this.arcs.push({ points, color: ARC_COLOR[arc.terminator] });
      this.junctions.push({
        position: points[points.length - 1],
        color: arc.terminator === 'impact' ? ARC_COLOR.impact : JUNCTION_COLOR,
      });
    }
  }

  dispose(): void {
    for (const line of this.lines) line.dispose();
    this.markers.dispose();
  }
}
